// src/App.js
import React from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate } from 'react-router-dom';
import './App.css';
import { ThemeProvider } from '@mui/material/styles';
import theme from './theme/theme';
import UserView from './components/UserView';
import TranscriptionView from './components/TranscriptionView';
import TranscriptionsListView from './components/TranscriptionsListView';
import TranscriptionResultView from './components/TranscriptionResultView';
import Login from './components/Auth/Login';
import AfterLogin from './components/Auth/AfterLogin';
import AuthStatusScreen from './components/Auth/AuthStatusScreen';
import ProtectedRoute from './Routes/ProtectedRoute';

// MSAL-Imports
import { MsalProvider, useIsAuthenticated } from '@azure/msal-react';
import { msalInstance } from './auth/msalInstance';

import { TranscriptionProvider } from './context/TranscriptionContext';
import TranscriptionOverlay from './components/TranscriptionOverlay';
import NavigationBar from './components/NavigationBar';
import Footer from './components/Footer';
import { SnackbarProvider } from 'notistack';

import OneClickSettingsView from './components/Settings/OneClickSettingsView';
import WhatsNewModal from './components/WhatsNewModal';
import LicensesView from './components/LicensesView';
import AdminView from './components/Admin/AdminView';

function App() {
  const [msalInitialized, setMsalInitialized] = React.useState(false);

  // Initialize MSAL instance
  React.useEffect(() => {
    const initializeMsal = async () => {
      try {
        await msalInstance.initialize();
        console.log("MSAL initialized successfully");
        setMsalInitialized(true);
        
        // Handle any pending redirects
        const redirectResult = await msalInstance.handleRedirectPromise();
        if (redirectResult) {
          console.log("Redirect result handled:", redirectResult.account?.username);
        }
      } catch (error) {
        console.error("MSAL initialization failed:", error);
        setMsalInitialized(true); // Set to true even on error to prevent infinite loading
      }
    };
    
    initializeMsal();
  }, []);

  // Show loading while MSAL initializes
  if (!msalInitialized) {
    return (
      <AuthStatusScreen
        title="Authentifizierung wird vorbereitet"
        subtitle="Die sichere Anmeldung wird initialisiert."
      />
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <MsalProvider instance={msalInstance}>
        <Router>
          <SnackbarProvider maxSnack={3}>
            <TranscriptionProvider>
              <AuthenticatedOverlay />
              <AppContent />
            </TranscriptionProvider>
          </SnackbarProvider>
        </Router>
      </MsalProvider>
    </ThemeProvider>
  );
}

// SEC-037: Overlay nur fuer eingeloggte User rendern, sonst leakt die
// Job-Queue Dateinamen + IDs auf die Login-Page (Info-Disclosure).
function AuthenticatedOverlay() {
  const isAuthenticated = useIsAuthenticated();
  return isAuthenticated ? <TranscriptionOverlay /> : null;
}

function AppContent() {

  return (
    <div className="App">
      <WhatsNewModal />

      {/* Navigation Bar (Menüband oben) */}
      <NavigationBar />

      {/* Haupt-Inhalt mit Routes */}
      <header className="App-main">
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/afterlogin" element={<AfterLogin />} />
          {/* Lizenzhinweise sind bewusst oeffentlich - der Footer verlinkt sie
              auch auf der Login-Seite. */}
          <Route path="/lizenzen" element={<LicensesView />} />
          <Route
            path="/transcribe"
            element={
              <ProtectedRoute>
                <TranscriptionView />
              </ProtectedRoute>
            }
          />
          <Route
            path="/transcription-result/:jobId"
            element={
              <ProtectedRoute>
                <TranscriptionResultView />
              </ProtectedRoute>
            }
          />
          <Route
            path="/transcriptions"
            element={
              <ProtectedRoute>
                <TranscriptionsListView />
              </ProtectedRoute>
            }
          />
          {/* Alte Adresse der Statistiken, jetzt ein Reiter im Admin-Bereich. */}
          <Route path="/stats" element={<Navigate to="/admin/statistiken" replace />} />
          <Route
            path="/settings/oneclick"
            element={
              <ProtectedRoute>
                <OneClickSettingsView />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/*"
            element={
              <ProtectedRoute>
                <AdminView />
              </ProtectedRoute>
            }
          />
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <UserView />
              </ProtectedRoute>
            }
          />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </header>

      {/* Platzfüller, damit Footer/Balken unten bleiben */}
      <div className="App-spacer" />

      {/* Footer mit Version, Links und Stats-Link */}
      <Footer />
    </div>
  );
}

export default App;
